import { type ClassValue, clsx } from "clsx";
import CryptoJS from "crypto-js";
import { twMerge } from "tailwind-merge";
import constants from "@/assets/constants";
import { EnvVariables } from "@/env-variables";
import { storeMasterDataState } from "@/stores/MasterDataState";

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs));
};

export const extractParams = (token: string) => {
  const singleDecodedToken = CryptoJS.enc.Base64.parse(token).toString(CryptoJS.enc.Utf8);
  const encryptedToken = CryptoJS.enc.Base64.parse(singleDecodedToken);
  const key = CryptoJS.enc.Utf8.parse(import.meta.env.VITE_ENCRYPTION_SECRET_KEY);
  const iv = CryptoJS.enc.Utf8.parse(import.meta.env.VITE_ENCRYPTION_SECRET_IV);

  const param = { ciphertext: encryptedToken } as CryptoJS.lib.CipherParams;

  const decryptedBytes = CryptoJS.AES.decrypt(param, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  const decryptedToken = JSON.parse(decryptedBytes.toString(CryptoJS.enc.Utf8));

  return decryptedToken;
};

export const encryptString = (input: string): string => {
  const key = CryptoJS.enc.Utf8.parse(EnvVariables.encryptionSecretKey);
  const iv = CryptoJS.enc.Utf8.parse(EnvVariables.encryptionSecretIV);

  const encrypted = CryptoJS.AES.encrypt(input, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return encrypted.toString();
};

export const decryptString = (encryptedString: string): string => {
  try {
    // Validate input
    if (!encryptedString || typeof encryptedString !== "string") {
      console.warn("Invalid encrypted string provided to decryptString");
      return "";
    }

    const key = CryptoJS.enc.Utf8.parse(EnvVariables.encryptionSecretKey);
    const iv = CryptoJS.enc.Utf8.parse(EnvVariables.encryptionSecretIV);

    const decryptedBytes = CryptoJS.AES.decrypt(encryptedString, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    // Check if decryption was successful before converting to string
    if (!decryptedBytes || decryptedBytes.sigBytes <= 0) {
      console.warn("Decryption failed or resulted in empty data - data may be corrupted");
      return "";
    }

    // Wrap the UTF-8 conversion in a try-catch to handle malformed data
    let decryptedString: string;
    try {
      decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);
    } catch (utf8Error) {
      console.warn("Failed to convert decrypted bytes to UTF-8 - data is corrupted:", utf8Error);
      return ""; // Return empty string instead of throwing
    }

    // Check if decryption resulted in valid UTF-8 data
    if (!decryptedString) {
      console.warn("Decryption resulted in empty string - data may be corrupted");
      return "";
    }

    return decryptedString;
  } catch (error) {
    console.error("Error decrypting string: ", error);
    // If decryption fails, it might be due to corrupted data
    // Clear the corrupted data to prevent repeated errors
    console.warn("Clearing potentially corrupted encrypted data");
    return "";
  }
};

export const parseGender = (gender: string) => {
  if (gender === "M") return "Male";
  if (gender === "F") return "Female";
  if (gender === "U") return "Unknown";
  return gender;
};

export const parseNricType = (nricType: string) => {
  const nricTypes = constants.NRIC_TYPES;
  const nricTypeObject = nricTypes.find((type) => type.value === nricType);
  return nricTypeObject?.label;
};

export const filterOptions = (
  input: string,
  option: { label?: string; value?: string } | undefined,
) => {
  return option && typeof option.label === "string"
    ? option.label.toLowerCase().includes(input.toLowerCase())
    : false;
};

export const mappingCountryCodeToDialCode = (countryCode: string) => {
  const nationality = storeMasterDataState.nationality;
  const country = nationality.find((country) => country.code === countryCode);
  return country?.dialCode;
};

export const mappingDialCodeToCountryCode = (dialCode: string) => {
  const nationality = storeMasterDataState.nationality;
  const country = nationality.find((country) => country.dialCode === dialCode);
  return country?.code;
};
